import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, LineChart, Bar, Line, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { Bell, Building, DoorOpen, Key, HousePlus, DoorClosed, HomeIcon, PlusCircle, Star, BarChart as BarChartIcon, Search, Construction } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar";
import AppSidebar from "@/components/AppSidebar";
import { useHomeDashboardData } from '@/hooks/useHomeDashboardData';
import { usePricePerSqMeterEvolution } from '@/hooks/usePricePerSqMeterEvolution';
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorDisplay } from '@/components/dashboard';
import { useAuth } from '@/context/AuthContext';
import PricePerSqMeterEvolutionChart from '@/components/dashboard/PricePerSqMeterEvolutionChart';

// Mapeamento dos nomes das cores do Tailwind para valores hexadecimais
const TAILWIND_COLORS = {
  'blue-base': '#1e90ff',
  'blue-muted': '#4682b4',
  'blue-deep': '#191970',
  'blue-accent': '#00b7eb',
  'neutral-light': '#e6e9f0',
  'neutral-dark': '#2f4f4f',
  'highlight': '#ffa500',
};

// Usando o mapeamento para as cores
const COLORS = [
  TAILWIND_COLORS['blue-base'],
  TAILWIND_COLORS['blue-muted'],
  TAILWIND_COLORS['blue-accent'],
  TAILWIND_COLORS['blue-deep'],
];

const marketUpdates = [
  { id: 1, text: 'Criação do dashboard de imóveis que entraram no mercado recentemente ', time: '15/06/2025' },
  { id: 2, text: 'Atualizações na Análise de Imóveis, com melhores recomendações e interface moderna', time: '04/05/2025' },
  { id: 3, text: 'Inclusão do Dashboard de análise de Imobiliárias', time: '28/04/2025' },
  { id: 4, text: 'Implementação do novo Dashboard de Imóveis que Saíram do Mercado na última semana, entenda o que está acontecendo.', time: '21/04/2025' },
  { id: 5, text: 'Bases atualizadas às segundas-feiras pela manhã', time: '15/03/2025' },
];

const Counter = ({ icon: Icon, title, value, trend, color = "text-blue-dark", loading = false, comingSoon = false }) => {
  return (
    <Card className="transition-all duration-300 hover:shadow-md">
      <CardContent className="p-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {loading ? (
              <Skeleton className="h-8 w-24 mt-1" />
            ) : comingSoon ? (
              <div className="flex items-center gap-2 mt-1">
                <p className="text-2xl font-bold text-amber-500">Em breve</p>
                <Construction className="h-5 w-5 text-amber-500" />
              </div>
            ) : (
              <p className={`text-2xl font-bold mt-1 ${color}`}>{value.toLocaleString('pt-BR')}</p>
            )}
            {trend && !comingSoon && !loading && (
              <p className={`text-xs mt-1`}>
                {trend} vs. mês anterior
              </p>
            )}
          </div>
          <div className={`bg-blue-light/10 p-3 rounded-lg w-12 h-12 flex items-center justify-center`}>
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const FeatureCard = ({ icon: Icon, title, description, onClick = () => {}, disabled = false }) => {
  return (
    <Card 
      className={`transition-all duration-300 hover:shadow-md ${!disabled ? 'cursor-pointer hover:-translate-y-1' : 'opacity-70'}`}
      onClick={!disabled ? onClick : undefined}
    >
      <CardContent className="p-6">
        <div className="flex flex-col items-center text-center">
          <div className="bg-blue-light/10 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
            <Icon className="h-6 w-6 text-blue-dark" />
          </div>
          <CardTitle className="text-lg mb-2">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
          {disabled && <p className="text-xs mt-2 text-amber-600">Em breve</p>}
        </div>
      </CardContent>
    </Card>
  );
};

const Home = () => {
  const [marketFilter, setMarketFilter] = useState("venda");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { state } = useSidebar();
  const { checkSessionHealth } = useAuth();
  const [loadingStages, setLoadingStages] = useState({
    basicInfo: true,
    marketData: true,
    charts: true
  });
  const { data, loading, error, refetch } = useHomeDashboardData();
  const { data: pricePerSqMeterData, isLoading: pricePerSqMeterLoading, error: pricePerSqMeterError } = usePricePerSqMeterEvolution(marketFilter as 'venda' | 'aluguel');

  console.log('[Home] Rendering with data:', data, 'loading:', loading, 'error:', error);

  // Check session health when component loads to ensure authentication is valid
  useEffect(() => {
    console.log('Checking session health on home page load');
    checkSessionHealth();
    
    // Simulate progressive loading - counters appear first, then market data
    setTimeout(() => setLoadingStages(prev => ({ ...prev, basicInfo: false })), 300);
    setTimeout(() => setLoadingStages(prev => ({ ...prev, marketData: false })), 800);
    setTimeout(() => setLoadingStages(prev => ({ ...prev, charts: false })), 1500);
    
  }, [checkSessionHealth]);

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1).replace('.0', '')} mi`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)} mil`;
    }
    return value;
  };

  // Prepare chart data based on database data
  const priceEvolutionData = marketFilter === 'venda' 
    ? data?.venda_evolucao || []
    : data?.aluguel_evolucao || [];

  const propertyTypeData = marketFilter === 'venda'
    ? data?.venda_tipos?.slice(0, 5).map(item => ({
        name: item.tipo || 'Não especificado',
        value: item.count
      })) || []
    : data?.aluguel_tipos?.slice(0, 5).map(item => ({
        name: item.tipo || 'Não especificado',
        value: item.count
      })) || [];

  return (
    <div className="flex min-h-screen w-full bg-white">
      <AppSidebar activePage="home" />
      
      <div className={`flex-1 overflow-auto p-6 ${state === "collapsed" ? "ml-10" : ""} ${isMobile ? "mt-14" : ""}`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Bem-vindo ao Broker Base!</h1>
            <p className="text-muted-foreground">Seu painel de controle do mercado imobiliário</p>
          </div>
        </div>
        
        {error && (
          <div className="mb-6">
            <ErrorDisplay error={error} onRetry={refetch} />
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Counter 
            icon={HousePlus} 
            title="Imóveis à Venda" 
            value={data?.venda_count || 0} 
            trend="-" 
            loading={loading || loadingStages.basicInfo}
          />
          <Counter 
            icon={Key} 
            title="Imóveis para Aluguel" 
            value={data?.aluguel_count || 0} 
            trend="-" 
            loading={loading || loadingStages.basicInfo}
          />
          <Counter 
            icon={DoorClosed} 
            title="Entraram no Mercado" 
            value={0} 
            trend="-"
            comingSoon={true}
            loading={loading || loadingStages.basicInfo}
          />
          <Counter 
            icon={DoorOpen} 
            title="Saíram do Mercado" 
            value={0} 
            trend="-"
            comingSoon={true}
            loading={loading || loadingStages.basicInfo}
          />
        </div>
        
        <div className="mb-6">
          <Tabs defaultValue="venda" onValueChange={setMarketFilter}>
            <TabsList>
              <TabsTrigger value="venda">Mercado de Venda</TabsTrigger>
              <TabsTrigger value="aluguel">Mercado de Aluguel</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Evolução do Preço Médio</CardTitle>
              <CardDescription>
                {marketFilter === "venda" 
                  ? "Valores médios de venda (R$)" 
                  : "Valores médios de aluguel (R$)"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(loading || loadingStages.charts) ? (
                <div className="h-[300px] flex items-center justify-center">
                  <Skeleton className="h-[250px] w-full" />
                </div>
              ) : priceEvolutionData.length === 0 ? (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Não há dados suficientes para exibir o gráfico
                </div>
              ) : (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={priceEvolutionData}
                      margin={isMobile ? { top: 5, right: 10, left: 0, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={TAILWIND_COLORS['neutral-light']} opacity={0.5} />
                      <XAxis 
                        dataKey="scrape_date_formatted" 
                        stroke={TAILWIND_COLORS['neutral-dark']}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        tickMargin={isMobile ? 5 : 10}
                      />
                      <YAxis 
                        tickFormatter={(value) => formatCurrency(value)}
                        stroke={TAILWIND_COLORS['neutral-dark']}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        width={isMobile ? 45 : 60}
                        tickMargin={isMobile ? 2 : 5}
                      />
                      <RechartsTooltip 
                        formatter={(value) => [
                          `R$ ${Number(value).toLocaleString('pt-BR')}`, 
                          "Preço Médio"
                        ]}
                        contentStyle={isMobile ? { fontSize: '12px', padding: '4px' } : {}}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="avg_price" 
                        name="Preço Médio"
                        stroke={TAILWIND_COLORS['blue-base']}
                        strokeWidth={2}
                        dot={{ r: isMobile ? 3 : 4, fill: TAILWIND_COLORS['blue-base'], strokeWidth: 1 }}
                        activeDot={{ r: isMobile ? 5 : 6 }}
                        animationDuration={1500} 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Distribuição de Imóveis por Tipo</CardTitle>
              <CardDescription>
                {marketFilter === "venda" 
                  ? "Número de imóveis por categoria à venda" 
                  : "Número de imóveis por categoria para aluguel"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(loading || loadingStages.charts) ? (
                <div className="h-[300px] flex items-center justify-center">
                  <Skeleton className="h-[250px] w-full" />
                </div>
              ) : propertyTypeData.length === 0 ? (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Não há dados suficientes para exibir o gráfico
                </div>
              ) : (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={propertyTypeData}
                      margin={isMobile ? { top: 5, right: 10, left: 0, bottom: 20 } : { top: 5, right: 30, left: 20, bottom: 5 }}
                      layout={isMobile ? "vertical" : "horizontal"}
                    >
                      <CartesianGrid 
                        strokeDasharray="3 3" 
                        vertical={!isMobile} 
                        horizontal={isMobile} 
                        stroke={TAILWIND_COLORS['neutral-light']} 
                        opacity={0.5} 
                      />
                      {isMobile ? (
                        <>
                          <XAxis 
                            type="number"
                            stroke={TAILWIND_COLORS['neutral-dark']} 
                            tick={{ fontSize: 10 }}
                          />
                          <YAxis 
                            dataKey="name"
                            type="category"
                            width={75} 
                            stroke={TAILWIND_COLORS['neutral-dark']} 
                            tick={{ fontSize: 10 }}
                          />
                        </>
                      ) : (
                        <>
                          <XAxis 
                            dataKey="name" 
                            stroke={TAILWIND_COLORS['neutral-dark']} 
                          />
                          <YAxis 
                            stroke={TAILWIND_COLORS['neutral-dark']} 
                          />
                        </>
                      )}
                      <RechartsTooltip 
                        formatter={(value) => [`${value} imóveis`, "Quantidade"]}
                        contentStyle={isMobile ? { fontSize: '12px', padding: '4px' } : {}}
                      />
                      <Bar 
                        dataKey="value" 
                        fill={TAILWIND_COLORS['blue-base']}
                        name="Quantidade"
                        radius={isMobile ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                      >
                        {propertyTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <h2 className="text-xl font-semibold mb-4">Dashboards e Análises de Mercado</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <FeatureCard 
            icon={Search} 
            title="Análise Inteligente de Propriedades" 
            description="Analise um imóvel com nossa IA" 
            onClick={() => navigate('/analisar-imovel')}
          />
          <FeatureCard 
            icon={HousePlus} 
            title="Dashboard de Venda" 
            description="Entenda o mercado de vendas em detalhes" 
            onClick={() => navigate('/dashboard-venda')}
          />
          <FeatureCard 
            icon={Key} 
            title="Dashboard de Aluguel" 
            description="Entenda o mercado de aluguéis em detalhes" 
            onClick={() => navigate('/dashboard-aluguel')}
          />
          <FeatureCard 
            icon={DoorOpen} 
            title="Imóveis que saíram do Mercado" 
            description="Entenda o o que movimentou o mercado de vendas na última semana" 
            onClick={() => navigate('/dashboard-sairam-ultima-semana')}
          />
           <FeatureCard 
            icon={DoorClosed} 
            title="Imóveis que entraram no Mercado" 
            description="Entenda o o que movimentou o mercado de vendas na última semana" 
            onClick={() => navigate('/dashboard-entraram-ultima-semana')}
          />
          <FeatureCard 
            icon={Building} 
            title="Análise de Imobiliárias" 
            description="Entenda a atuação das 100 maiores imobiliárias em vendas ou aluguéis" 
            onClick={() => navigate('/agency-analytics')}
          />
        </div>
        
        <div className="mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Evolução do Preço por m² - Últimas 12 Semanas</CardTitle>
              <CardDescription>
                {marketFilter === "venda" 
                  ? "Preço médio por metro quadrado no mercado de vendas" 
                  : "Preço médio por metro quadrado no mercado de aluguéis"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(pricePerSqMeterLoading || loadingStages.charts) ? (
                <div className="h-[300px] flex items-center justify-center">
                  <Skeleton className="h-[250px] w-full" />
                </div>
              ) : pricePerSqMeterError ? (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Erro ao carregar dados: {pricePerSqMeterError.message}
                </div>
              ) : !pricePerSqMeterData?.months || pricePerSqMeterData.months.length === 0 ? (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Dados insuficientes para exibir o gráfico
                </div>
              ) : (
                <div className="h-[300px]">
                  <PricePerSqMeterEvolutionChart 
                    data={pricePerSqMeterData.months}
                    isMobile={isMobile}
                    dashboardType={marketFilter as 'venda' | 'aluguel'}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {!loadingStages.marketData && (
          <>
            <h2 className="text-xl font-semibold mb-4">Atualizações do Mercado</h2>
            <Card>
              <CardContent className="p-6">
                <ul className="space-y-4">
                  {marketUpdates.map((update) => (
                    <li key={update.id} className="flex gap-3 pb-3 border-b last:border-0 last:pb-0">
                      <div className="p-2 bg-blue-light bg-opacity-20 rounded-full h-fit">
                        <Bell size={16} className="text-blue-dark" />
                      </div>
                      <div>
                        <p className="text-sm">{update.text}</p>
                        <p className="text-xs text-muted-foreground mt-1">{update.time}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Home;
